import { useMemo, useCallback, useState, useRef, useEffect, type MouseEvent } from 'react'
import {
  Archive,
  CheckCheck,
  ChevronDown,
  Inbox,
  MoreVertical,
  PanelRight,
  RefreshCw,
  RotateCcw,
  SlidersHorizontal,
} from 'lucide-react'
import { Panel, PanelGroup } from 'react-resizable-panels'
import { toast } from 'sonner'
import { useInbox } from '@/hooks/use-inbox'
import { useInboxDenseLayout } from '@/hooks/use-inbox-dense-layout'
import { useInboxGroupByWorkspace } from '@/hooks/use-inbox-group-by-workspace'
import { useWorkspace } from '@/providers/workspace-provider'
import { useChatSurfaceRegistryOptional } from '@/hooks/use-chat-surface-registry'
import { openAgentChatThread } from '@/lib/open-agent-chat-thread'
import { openInboxThreadInChat } from '@/lib/open-inbox-thread-in-chat'
import { getSoulProposalContextDoc } from '@/components/features/inbox/SoulProposalActionBar'
import { AddressBar } from '@/components/ui/address-bar'
import { IconButton } from '@/components/ui/icon-button'
import {
  ExtensionFilterBar,
  IndexSurfaceShell,
  SecondaryPreviewResizeHandle,
} from '@/components/integrations/_shared'
import { PanelSearchBar } from '@/components/ui/panel-search-bar'
import { usePanelSearch } from '@/hooks/use-panel-search'
import { SidebarEmptyState } from '@/components/layout/sidebar/SidebarEmptyState'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { ListSurfaceStatusBar } from '@/components/ui/list-surface-status-bar'
import { InboxThreadRow } from './InboxFeed'
import { InboxReader } from './InboxReader'
import { InboxPasswordSetupBanner } from './InboxPasswordSetupBanner'
import {
  mapInboxItemToThread,
  mapThreadsRowToThread,
  mapWorkspaceToInbox,
  resolveThreadWorkspace,
} from './mappers'
import { filterThreadsByFolder, sortInboxThreads } from './inbox-folder-filters'
import {
  folderToApiKind,
  folderToApiStatus,
  inboxFolderLabel,
  inboxIndexCrumbs,
  type InboxListFocus,
  type InboxSortDir,
} from './inbox-folder-types'
import { useInboxFolderSelection } from './use-inbox-folder-selection'
import { listAgentChatThreadsPage, listThreads } from '@/lib/api/threads'
import type { Banner, Status, Thread } from './types'
import type { OpenIntentClickEvent } from '@/lib/open-intent'
import { testIds } from '@/lib/test-ids'
import { useNeedsPassword } from '@/hooks/use-needs-password'
import { cn } from '@/lib/utils'
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from '@/lib/menu-chrome'

function SnoozeBanner({ label, hint, onUndo }: { label: string; hint: string; onUndo: () => void }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border-b border-amber-500/20 shrink-0">
      <span className="text-xs text-amber-600 flex-1">Snoozed: {label} ({hint})</span>
      <button type="button" onClick={onUndo} className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700">
        <RotateCcw className="h-3 w-3" /> Undo
      </button>
    </div>
  )
}

function ArchiveBanner({ onUndo }: { onUndo: () => void }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-muted/60 border-b border-border shrink-0">
      <Archive className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <span className="text-xs text-muted-foreground flex-1">Thread archived</span>
      <button type="button" onClick={onUndo} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <RotateCcw className="h-3 w-3" /> Undo
      </button>
    </div>
  )
}

const FOCUS_CHIPS = [
  { id: 'unread', label: 'Unread', testId: 'inbox-focus-unread' },
  { id: 'all', label: 'All', testId: 'inbox-focus-all' },
]

/**
 * Main-panel Inbox thread index (DocumentList-density) with PanelRight preview.
 * Folder nouns live in the mode panel; this surface owns the thread list.
 */
export function InboxFoldersIndex() {
  const registry = useChatSurfaceRegistryOptional()
  const { allItems, isLoading, archive, markRead, markUnread, refetch, markAllRead, hydrateThread } = useInbox()
  const { workspaces: appWorkspaces, activeWorkspaceIds } = useWorkspace()
  const { folderId, listFocus, activeExtension, selectFolder, selectListFocus } =
    useInboxFolderSelection()
  const { dense, setDense } = useInboxDenseLayout(false)
  const { groupByWorkspace, setGroupByWorkspace } = useInboxGroupByWorkspace(true)
  const { needsPassword, loading: needsPasswordLoading, dismiss: dismissPasswordPrompt } = useNeedsPassword()

  const allThreads = useMemo(() => allItems.map(mapInboxItemToThread), [allItems])
  const inboxWorkspaces = useMemo(
    () => appWorkspaces.map((ws) => mapWorkspaceToInbox(ws, activeWorkspaceIds)),
    [appWorkspaces, activeWorkspaceIds],
  )

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(true)
  const [sortDir, setSortDir] = useState<InboxSortDir>('newest')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const indexRootRef = useRef<HTMLDivElement>(null)
  const {
    searchOpen,
    searchQuery,
    setSearchQuery,
    inputRef: inboxSearchRef,
    close: closeInboxSearch,
    open: openInboxSearch,
  } = usePanelSearch()
  const [banner, setBanner] = useState<Banner | null>(null)
  const bannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [snoozedIds, setSnoozedIds] = useState<Set<string>>(new Set())
  const [snoozedUntilMap, setSnoozedUntilMap] = useState<Record<string, string>>({})
  /** ADR-0015: agent_chat is excluded from useInbox triage fetch — load separately for Chats folder. */
  const [chatFolderThreads, setChatFolderThreads] = useState<Thread[]>([])
  const [chatsLoading, setChatsLoading] = useState(false)
  const [chatsReloadToken, setChatsReloadToken] = useState(0)
  /** API search hits for triage folders (body match beyond local 100-row window). */
  const [searchThreads, setSearchThreads] = useState<Thread[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchReloadToken, setSearchReloadToken] = useState(0)

  const threadsWithSnooze = useMemo(() => {
    return allThreads.map((t) => {
      if (snoozedIds.has(t.id)) {
        return { ...t, status: 'snoozed' as Status, snoozedUntil: snoozedUntilMap[t.id] }
      }
      return t
    })
  }, [allThreads, snoozedIds, snoozedUntilMap])

  const trimmedSearch = searchQuery.trim()
  const apiSearchActive = trimmedSearch.length > 0 && folderId !== 'chats'

  useEffect(() => {
    if (folderId !== 'chats') return
    let cancelled = false
    const delayMs = trimmedSearch ? 300 : 0
    const timer = window.setTimeout(() => {
      setChatsLoading(true)
      void listAgentChatThreadsPage({
        search: trimmedSearch || undefined,
        limit: 100,
        offset: 0,
      })
        .then((page) => {
          if (cancelled) return
          setChatFolderThreads(
            page.threads.map((row) =>
              mapThreadsRowToThread(row as unknown as Record<string, unknown>),
            ),
          )
        })
        .catch((err) => {
          if (cancelled) return
          console.error('[InboxFoldersIndex] Failed to load chats folder:', err)
          toast.error('Failed to load chats')
          setChatFolderThreads([])
        })
        .finally(() => {
          if (!cancelled) setChatsLoading(false)
        })
    }, delayMs)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [folderId, trimmedSearch, chatsReloadToken])

  useEffect(() => {
    if (!apiSearchActive) {
      setSearchThreads([])
      setSearchLoading(false)
      return
    }
    let cancelled = false
    const timer = window.setTimeout(() => {
      setSearchLoading(true)
      const kind = folderToApiKind(folderId)
      const status =
        folderToApiStatus(folderId) ??
        (listFocus === 'unread' && folderId !== 'archived' && folderId !== 'snoozed'
          ? 'unread'
          : undefined)
      void listThreads({
        search: trimmedSearch,
        limit: 100,
        offset: 0,
        kind,
        status,
        excludeKind: 'agent_chat',
      })
        .then((page) => {
          if (cancelled) return
          setSearchThreads(
            page.threads.map((row) =>
              mapThreadsRowToThread(row as unknown as Record<string, unknown>),
            ),
          )
        })
        .catch((err) => {
          if (cancelled) return
          console.error('[InboxFoldersIndex] Failed to search threads:', err)
          toast.error('Failed to search inbox')
          setSearchThreads([])
        })
        .finally(() => {
          if (!cancelled) setSearchLoading(false)
        })
    }, 300)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [apiSearchActive, trimmedSearch, folderId, listFocus, searchReloadToken])

  const refreshList = useCallback(() => {
    if (folderId === 'chats') {
      setChatsReloadToken((n) => n + 1)
      return
    }
    if (apiSearchActive) {
      setSearchReloadToken((n) => n + 1)
    }
    void refetch()
  }, [folderId, apiSearchActive, refetch])

  const sourceThreads = useMemo(() => {
    if (folderId === 'chats') return chatFolderThreads
    if (apiSearchActive) return searchThreads
    return threadsWithSnooze
  }, [folderId, chatFolderThreads, apiSearchActive, searchThreads, threadsWithSnooze])

  /** Folder + focus, no text search (denominator for N of M). */
  const folderScopedThreads = useMemo(() => {
    const base =
      folderId === 'chats'
        ? chatFolderThreads
        : threadsWithSnooze
    return filterThreadsByFolder(base, folderId, {
      activeWorkspaceIds,
      activeExtension,
      searchQuery: '',
      listFocus,
    })
  }, [
    folderId,
    chatFolderThreads,
    threadsWithSnooze,
    activeWorkspaceIds,
    activeExtension,
    listFocus,
  ])

  const visibleThreads = useMemo(() => {
    const filtered = filterThreadsByFolder(sourceThreads, folderId, {
      activeWorkspaceIds,
      activeExtension,
      // API / chats search already applied server-side; avoid double-narrowing titles only.
      searchQuery: apiSearchActive || folderId === 'chats' ? '' : searchQuery,
      listFocus,
    })
    return sortInboxThreads(filtered, sortDir)
  }, [
    sourceThreads,
    folderId,
    activeWorkspaceIds,
    activeExtension,
    apiSearchActive,
    searchQuery,
    listFocus,
    sortDir,
  ])

  const listLoading =
    folderId === 'chats' ? chatsLoading : apiSearchActive ? searchLoading : isLoading

  const threadById = useCallback(
    (id: string) => {
      const pools = [visibleThreads, sourceThreads, threadsWithSnooze, chatFolderThreads]
      for (const pool of pools) {
        const hit = pool.find((t) => t.id === id)
        if (hit) return hit
      }
      return undefined
    },
    [visibleThreads, sourceThreads, threadsWithSnooze, chatFolderThreads],
  )

  // Sticky preview: keep the selected thread even if it leaves the current folder
  // filter (e.g. status change). List highlight still comes from visibleThreads.
  const selectedThread = useMemo(() => {
    if (!selectedId) return null
    return (
      visibleThreads.find((t) => t.id === selectedId)
      ?? threadById(selectedId)
      ?? null
    )
  }, [visibleThreads, selectedId, threadById])

  const workspaceAccent = useMemo(() => {
    if (!selectedThread) return null
    const w = resolveThreadWorkspace(selectedThread, inboxWorkspaces)
    if (!w) return null
    return { label: w.label, color: w.color }
  }, [selectedThread, inboxWorkspaces])

  const showBanner = useCallback((b: Banner) => {
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current)
    setBanner(b)
    bannerTimerRef.current = setTimeout(() => setBanner(null), 5000)
  }, [])

  const handleArchive = useCallback(
    (id: string) => {
      archive(id)
      if (selectedId === id) setSelectedId(null)
      showBanner({ type: 'archive', threadId: id })
    },
    [archive, selectedId, showBanner],
  )

  const handleSnooze = useCallback((id: string, label: string, hint: string) => {
    setSnoozedIds((prev) => new Set([...prev, id]))
    setSnoozedUntilMap((prev) => ({ ...prev, [id]: hint }))
    if (selectedId === id) setSelectedId(null)
    showBanner({ type: 'snooze', threadId: id, label, hint })
  }, [selectedId, showBanner])

  const handleUndo = useCallback(() => {
    if (!banner) return
    if (banner.type === 'snooze') {
      setSnoozedIds((prev) => {
        const next = new Set(prev)
        next.delete(banner.threadId)
        return next
      })
    }
    setBanner(null)
  }, [banner])

  const handleUnarchive = useCallback(() => {
    void refetch()
  }, [refetch])

  const openThread = useCallback(
    (id: string, event?: MouseEvent) => {
      const row = threadById(id)
      // Continuity: mark read on open (full tab / chat), not on select/preview.
      markRead(id)

      const clickEvent: OpenIntentClickEvent = event
        ? { metaKey: event.metaKey, ctrlKey: event.ctrlKey }
        : undefined

      if (row?.kind === 'chat') {
        openAgentChatThread(registry, id, { title: row.title, clickEvent })
        return
      }

      window.dispatchEvent(
        new CustomEvent('open-inbox-thread-main', {
          detail: {
            threadId: id,
            title: row?.title,
            workspaceId: row?.workspaceId,
            clickEvent,
          },
        }),
      )
    },
    [registry, threadById, markRead],
  )

  const handleSelect = useCallback((id: string) => {
    // Continuity: click = select + preview only. Do not markRead here -
    // that removed the row from Unread and cleared selection/preview.
    setSelectedId(id)
  }, [])

  const handleOpenInChat = useCallback(
    async (threadId: string) => {
      const thr = threadById(threadId)
      const soulCtx = thr ? getSoulProposalContextDoc(thr) : null
      const linked = await openInboxThreadInChat({
        threadId,
        title: thr?.title,
        linkedConversationId: thr?.linkedConversationId,
        hydrate: hydrateThread,
        registry,
        forceAgentPanel: true,
        contextDocumentId: soulCtx?.documentId,
        contextDocumentTitle: soulCtx?.title,
      })
      if (!linked) toast.error('Could not open chat for this thread')
    },
    [registry, threadById, hydrateThread],
  )

  const clearFilters = useCallback(() => {
    selectListFocus('all')
    setSortDir('newest')
    setGroupByWorkspace(false)
    setFiltersOpen(false)
  }, [selectListFocus, setGroupByWorkspace])

  const workspaceCount = useMemo(() => {
    const ids = new Set(
      folderScopedThreads.map((t) => t.workspaceId || 'personal'),
    )
    return ids.size
  }, [folderScopedThreads])
  /** Group-by-workspace only changes the list when 2+ workspaces are in view. */
  const groupHeaders = groupByWorkspace && workspaceCount > 1
  const filtersActive = listFocus !== 'all' || sortDir !== 'newest' || groupHeaders

  const crumbSegments = useMemo(
    () =>
      inboxIndexCrumbs(folderId, {
        onInboxClick: () => selectFolder('inbox'),
      }),
    [folderId, selectFolder],
  )

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'f' || e.shiftKey || e.altKey) {
        return
      }
      const root = indexRootRef.current
      if (!root || root.getClientRects().length === 0) return
      const target = e.target instanceof HTMLElement ? e.target : null
      if (target && root.contains(target)) {
        const inSearch = Boolean(inboxSearchRef.current?.contains(target))
        const editing =
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable
        if (editing && !inSearch) return
      } else if (target) {
        const foreignEdit =
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable
        if (foreignEdit) return
      }
      e.preventDefault()
      e.stopPropagation()
      openInboxSearch()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [openInboxSearch, inboxSearchRef])

  const folderLabel = inboxFolderLabel(folderId === 'unread' ? 'inbox' : folderId)
  const feedBanner = banner ? { type: banner.type, label: banner.label, hint: banner.hint } : null

  type ThreadGroup = { key: string; label: string | null; color?: string; threads: Thread[] }

  const threadGroups = useMemo((): ThreadGroup[] => {
    if (!groupHeaders) {
      return [{ key: 'all', label: null, threads: visibleThreads }]
    }
    const byKey = new Map<string, ThreadGroup>()
    for (const t of visibleThreads) {
      const ws = resolveThreadWorkspace(t, inboxWorkspaces)
      const key = ws?.id ?? t.workspaceId ?? 'personal'
      const label = ws?.label ?? (key === 'personal' ? 'Personal' : 'Workspace')
      let group = byKey.get(key)
      if (!group) {
        group = { key, label, color: ws?.color, threads: [] }
        byKey.set(key, group)
      }
      group.threads.push(t)
    }
    return Array.from(byKey.values()).sort((a, b) => {
      if (a.key === 'personal') return 1
      if (b.key === 'personal') return -1
      return (a.label ?? '').localeCompare(b.label ?? '')
    })
  }, [groupHeaders, visibleThreads, inboxWorkspaces])

  return (
    <div ref={indexRootRef} className="flex h-full min-h-0 w-full flex-col">
    <IndexSurfaceShell
      className="min-h-0 flex-1"
      data-testid={testIds.inbox.foldersIndex}
      header={
        <nav
          className="flex items-center gap-2 px-4 py-2 border-b border-border bg-background/50 shrink-0"
          aria-label="Inbox index"
          data-testid={testIds.inbox.foldersIndexToolbar}
        >
          <div className="min-w-0 flex-1">
            <AddressBar segments={crumbSegments} containerWidth={null} />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton icon={<MoreVertical className="h-4 w-4" />} label="Inbox options" variant="ghost" size="sm" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
              collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
            >
              <DropdownMenuItem onClick={openInboxSearch}>
                Search
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setPreviewOpen((v) => !v)}>
                <PanelRight className="mr-2 h-4 w-4" />
                {previewOpen ? 'Hide preview pane' : 'Show preview pane'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={refreshList}>
                <RefreshCw className="mr-2 h-4 w-4" /> Refresh
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setDense(false)} className={cn(!dense && 'text-primary')}>
                Comfortable rows
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setDense(true)} className={cn(dense && 'text-primary')}>
                Dense rows
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => markAllRead()} disabled={!markAllRead}>
                <CheckCheck className="mr-2 h-4 w-4" /> Mark all read
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>
      }
      search={
        searchOpen ? (
          <PanelSearchBar
            inputRef={inboxSearchRef}
            value={searchQuery}
            onChange={setSearchQuery}
            onClose={closeInboxSearch}
            placeholder="Filter threads..."
            resultCount={visibleThreads.length}
            totalCount={Math.max(visibleThreads.length, folderScopedThreads.length)}
            countNoun="threads"
            data-testid={testIds.inbox.foldersSearchBar}
          />
        ) : null
      }
      footer={
        <ListSurfaceStatusBar
          includePreviewTip
          data-testid={testIds.inbox.foldersStatusBar}
          helpTestId={testIds.inbox.foldersStatusBarHelp}
          trailing={
            <span className="text-[10px] text-muted-foreground/40 tabular-nums">
              {visibleThreads.length} thread{visibleThreads.length !== 1 ? 's' : ''}
            </span>
          }
        />
      }
    >
      {!needsPasswordLoading && needsPassword && (
        <InboxPasswordSetupBanner onDismiss={dismissPasswordPrompt} />
      )}

      {feedBanner?.type === 'snooze' ? (
        <SnoozeBanner label={feedBanner.label ?? ''} hint={feedBanner.hint ?? ''} onUndo={handleUndo} />
      ) : null}
      {feedBanner?.type === 'archive' ? <ArchiveBanner onUndo={handleUndo} /> : null}

      <PanelGroup direction="horizontal" className="min-h-0 flex-1">
        <Panel minSize={30} className="flex min-h-0 min-w-0 flex-col overflow-hidden">
        <ExtensionFilterBar
          singleRow
          search=""
          onSearchChange={() => undefined}
          showSearch={false}
          chipStyle="segment"
          chips={FOCUS_CHIPS}
          activeChipId={listFocus}
          onChipChange={(id) => selectListFocus(id as InboxListFocus)}
          chipsTestId={testIds.inbox.foldersFilterChips}
          trailing={
            <div className="ml-auto flex items-center gap-2" data-testid={testIds.inbox.foldersFilterBar}>
              <Popover open={filtersOpen} onOpenChange={setFiltersOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      'h-7 gap-1 text-xs',
                      filtersActive && 'border-primary/50 text-primary',
                    )}
                    data-testid={testIds.inbox.foldersFiltersMenu}
                    aria-label="More filters"
                  >
                    <SlidersHorizontal className="h-3 w-3" />
                    Filters
                    {filtersActive ? (
                      <span className="ml-0.5 rounded-full bg-primary px-1 py-0 text-[10px] leading-4 text-primary-foreground">
                        {[listFocus !== 'all', sortDir !== 'newest', groupHeaders].filter(Boolean).length}
                      </span>
                    ) : null}
                    <ChevronDown className="h-3 w-3" aria-hidden />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-48 p-2">
                  <div className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Sort
                  </div>
                  {([
                    { id: 'newest' as const, label: 'Newest first' },
                    { id: 'oldest' as const, label: 'Oldest first' },
                  ]).map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => {
                        setSortDir(opt.id)
                        setFiltersOpen(false)
                      }}
                      className={cn(
                        'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted',
                        sortDir === opt.id && 'bg-muted font-medium',
                      )}
                    >
                      {sortDir === opt.id ? (
                        <span className="text-xs text-primary">✓</span>
                      ) : (
                        <span className="w-3.5" />
                      )}
                      {opt.label}
                    </button>
                  ))}
                  {workspaceCount > 1 ? (
                    <>
                      <div className="my-2 border-t border-border" />
                      <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted">
                        <Checkbox
                          checked={groupByWorkspace}
                          onCheckedChange={(v) => setGroupByWorkspace(v === true)}
                        />
                        Group by workspace
                      </label>
                    </>
                  ) : null}
                  {filtersActive ? (
                    <>
                      <div className="my-2 border-t border-border" />
                      <button
                        type="button"
                        onClick={clearFilters}
                        className="w-full rounded px-2 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      >
                        Clear filters
                      </button>
                    </>
                  ) : null}
                </PopoverContent>
              </Popover>
              {filtersActive ? (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-[11px] text-muted-foreground hover:text-foreground"
                >
                  Clear
                </button>
              ) : null}
            </div>
          }
        />
        <div
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          data-testid={testIds.inbox.foldersThreadList}
        >
          {listLoading && visibleThreads.length === 0 ? (
            <SidebarEmptyState
              icon={<RefreshCw className="h-8 w-8 animate-spin" />}
              title="Loading inbox…"
              description="Fetching threads from the API."
            />
          ) : folderId === 'archived' && visibleThreads.length === 0 ? (
            <SidebarEmptyState
              icon={<Archive className="h-8 w-8" />}
              title="Archive is empty"
              description="Archived threads live here, not deleted."
            />
          ) : visibleThreads.length === 0 ? (
            <SidebarEmptyState
              icon={<Inbox className="h-8 w-8" />}
              title={`No threads in ${folderLabel}`}
              description="Try another folder or clear your search filter."
            />
          ) : (
            <div className="flex-1 overflow-y-auto min-h-0">
              {threadGroups.map((group) => (
                <div key={group.key}>
                  {group.label ? (
                    <div
                      className="sticky top-0 z-[1] flex items-center gap-2 border-b border-border bg-muted/40 px-3 py-1.5"
                      data-testid={testIds.inbox.foldersGroupHeader(group.key)}
                    >
                      {group.color ? (
                        <span
                          className="h-1.5 w-1.5 shrink-0 rounded-full"
                          style={{ backgroundColor: group.color }}
                          aria-hidden
                        />
                      ) : null}
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {group.label}
                      </span>
                      <span className="text-[10px] tabular-nums text-muted-foreground/50">
                        {group.threads.length}
                      </span>
                    </div>
                  ) : null}
                  {group.threads.map((t) => (
                    <InboxThreadRow
                      key={t.id}
                      thread={t}
                      active={t.id === selectedId}
                      dense={dense}
                      onClick={() => handleSelect(t.id)}
                      onOpen={(event) => openThread(t.id, event)}
                      onArchive={handleArchive}
                      onSnooze={handleSnooze}
                      onMarkAsUnread={markUnread}
                      onMarkAsRead={markRead}
                      onPreview={(id) => {
                        setPreviewOpen(true)
                        handleSelect(id)
                      }}
                      onOpenInChat={(id) => {
                        void handleOpenInChat(id)
                      }}
                      workspace={resolveThreadWorkspace(t, inboxWorkspaces)}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
        </Panel>

        {previewOpen ? (
          <>
            <SecondaryPreviewResizeHandle data-testid={testIds.inbox.foldersPreviewResize} />
            <Panel
              defaultSize={38}
              minSize={22}
              maxSize={55}
              className="flex min-h-0 flex-col overflow-hidden"
              data-testid={testIds.inbox.foldersPreviewPanel}
            >
              <InboxReader
                thread={selectedThread}
                onClose={() => setPreviewOpen(false)}
                workspaceAccent={workspaceAccent}
                onArchive={handleArchive}
                onSnooze={handleSnooze}
                onUnarchive={handleUnarchive}
                onMarkAsUnread={markUnread}
                onMarkAsRead={markRead}
                onOpenInChat={handleOpenInChat}
                onOpenInNewTab={(id) => {
                  openThread(id, { metaKey: true, ctrlKey: false } as MouseEvent)
                }}
                onHydrate={(threadId) => hydrateThread(threadId).then((id) => id ?? undefined)}
                onDispatchStatusUpdated={() => void refetch()}
              />
            </Panel>
          </>
        ) : null}
      </PanelGroup>
    </IndexSurfaceShell>
    </div>
  )
}
